import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  CreateOrganizationContactRequest,
  UpdateOrganizationContactRequest,
  Organization,
  OrganizationsListResponse,
  OrganizationDetailResponse,
  AssignRoleRequest,
  AssignRoleResponse,
} from '@stfors/shared-types';
import { api, uploadFile } from '../client';

// Companies are admin-managed metadata; cache aggressively but invalidate
// on every mutation (and the directory aggregate) so fresh edits show up.
const STALE_MS = 5 * 60 * 1000;

export function useOrganizations(searchQuery?: string, role?: string) {
  const q = (searchQuery ?? '').trim();
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (role) params.set('role', role);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: ['organizations', { q: q || null, role: role || null }],
    queryFn: ({ signal }) => api.get<OrganizationsListResponse>(`/api/organizations${qs}`, signal),
    staleTime: STALE_MS,
  });
}

export function useOrganization(id: number | null) {
  return useQuery({
    enabled: id !== null && id > 0,
    queryKey: ['organization', id],
    queryFn: ({ signal }) =>
      api.get<OrganizationDetailResponse>(`/api/organizations/${id ?? 0}`, signal),
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrganizationRequest) =>
      api.post<OrganizationDetailResponse>('/api/organizations', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

export interface UpdateOrganizationInput extends UpdateOrganizationRequest {
  id: number;
}

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateOrganizationInput) =>
      api.patch<OrganizationDetailResponse>(`/api/organizations/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['organization', vars.id] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      // A company's name/contact fields are projected into its client /
      // contractor / vendor role views, so edits made on the org (Directory)
      // page must refresh those too — otherwise Clients shows stale company info.
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['contractors'] });
      void qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

// Attach a role to an existing company. Unbound — the org id rides in the
// mutation variables (known only after the create step in the Add flow).
export interface AssignOrgRoleInput extends AssignRoleRequest {
  orgId: number;
}
export function useAssignOrgRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, ...body }: AssignOrgRoleInput) =>
      api.post<AssignRoleResponse>(`/api/organizations/${orgId}/roles`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['organization', vars.orgId] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['contractors'] });
      void qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/organizations/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

// Set/replace a company's logo (raw image bytes → POST /:id/logo).
export function useSetOrgLogo(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadFile<void>(`/api/organizations/${id}/logo`, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organization', id] });
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

// Remove a company's logo (→ DELETE /:id/logo), reverting to the building glyph.
export function useDeleteOrgLogo(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>(`/api/organizations/${id}/logo`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organization', id] });
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

interface ContactsResponse {
  contacts: unknown[];
}

export function useAddOrgContact(orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrganizationContactRequest) =>
      api.post<ContactsResponse>(`/api/organizations/${orgId}/contacts`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organization', orgId] });
      // Contacts drive a company client's primary billing contact (synced
      // server-side), so keep the Clients view + Directory fresh too.
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

export interface UpdateOrgContactInput extends UpdateOrganizationContactRequest {
  contactId: number;
}

export function useUpdateOrgContact(orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, ...patch }: UpdateOrgContactInput) =>
      api.patch<ContactsResponse>(`/api/organizations/${orgId}/contacts/${contactId}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organization', orgId] });
      // Contacts drive a company client's primary billing contact (synced
      // server-side), so keep the Clients view + Directory fresh too.
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

export function useRemoveOrgContact(orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: number) =>
      api.delete<void>(`/api/organizations/${orgId}/contacts/${contactId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organization', orgId] });
      // Contacts drive a company client's primary billing contact (synced
      // server-side), so keep the Clients view + Directory fresh too.
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

// Display helper — prefer DBA when set, else legal name.
export function organizationDisplayName(o: Pick<Organization, 'name' | 'dba_name'>): string {
  const dba = o.dba_name?.trim();
  if (dba) return dba;
  return o.name;
}
